import { Link } from 'react-router-dom';
import { isExternalHref } from '../../utils/campaign';
import './HeroShowcase.css';

/**
 * One hero button. An absolute `href` is an off-site link and must not go
 * through the router.
 */
const HeroAction = ({ action, variant }) => {
  if (!action) return null;

  const className = `hero-showcase-btn hero-showcase-btn--${variant}`;

  if (isExternalHref(action.href)) {
    return (
      <a
        className={className}
        href={action.href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {action.label}
      </a>
    );
  }

  return (
    <Link className={className} to={action.href}>
      {action.label}
    </Link>
  );
};

/**
 * The hero's product showcase: copy on the left, the product standing on a
 * light ground to the right.
 *
 * Artwork is contained rather than cropped, so the slot is built for a
 * cut-out product shot - a full-bleed banner still renders, it just sits in
 * the right-hand column instead of filling the stage.
 *
 * A newline in `subtitle` breaks the tagline into its own lines; without one
 * the tagline simply wraps.
 */
const HeroShowcase = ({
  kicker,
  heading,
  headingAs: Heading = 'h2',
  subtitle,
  image,
  mobileImage,
  primary,
  secondary,
  eager = false,
}) => {
  const subtitleLines = String(subtitle || '')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);

  return (
    <div className={`hero-showcase${image ? '' : ' hero-showcase--no-media'}`}>
      <div className="hero-showcase-copy">
        {kicker && <span className="hero-showcase-kicker">{kicker}</span>}

        <Heading className="hero-showcase-title">{heading}</Heading>

        {subtitleLines.length > 0 && (
          <p className="hero-showcase-subtitle">
            {/* A fixed list of lines, so the index is a stable identity and
                two identical lines still get distinct keys. */}
            {subtitleLines.map((line, lineIndex) => (
              <span
                className="hero-showcase-subtitle-line"
                key={`${lineIndex}-${line}`}
              >
                {line}
              </span>
            ))}
          </p>
        )}

        <div className="hero-showcase-actions">
          <HeroAction action={primary} variant="primary" />
          <HeroAction action={secondary} variant="secondary" />
        </div>
      </div>

      {/* A product with no photo yet gets the copy on its own rather than a
          broken image where the product should stand. */}
      {image && (
        <div className="hero-showcase-media">
          <picture>
            {/* Matches the breakpoint the showcase stacks at, not the global
                768px one: between the two the layout was already stacked and
                portrait while still being served the wide desktop crop. */}
            {mobileImage && (
              <source media="(max-width: 860px)" srcSet={mobileImage} />
            )}
            <img
              src={image}
              alt={heading}
              className="hero-showcase-img"
              loading={eager ? 'eager' : 'lazy'}
              draggable="false"
            />
          </picture>
        </div>
      )}
    </div>
  );
};

export default HeroShowcase;
